import React from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import AppLayout from "layouts/app-layout";
import AuthLayout from "layouts/auth-layout";
import { useAuth } from "data/hooks/useAuth";
import Loading from "components/shared-components/Loading";
import AccessDenied from "views/auth-views/components/AccessDenied";
import roles from "roles";

export const APP_PREFIX_PATH = "/cms";
export const AUTH_PREFIX_PATH = "/auth";

// A wrapper that routes user to the dashboard when he/she lands on
// the login page while already authenticated
const CheckAlreadyAuthenticated = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  // Show nothing while loading
  if (loading) return null;

  // If authenticated and email verified, redirect to app
  if (isAuthenticated && user?.emailVerified) {
    return <Navigate to={APP_PREFIX_PATH} replace />;
  }

  // Otherwise show auth pages
  return children;
};

// A wrapper for <Route> that redirects to the login screen if you're not yet authenticated, and
// that lets only CMS roles in. The role used to be checked once, at login; any other signed-in
// account (a webshop customer, a scanner of the order picking system, a persisted session) could
// open every screen. Now it is checked on every route (planning/orderpicking-bouwplan-cms.md, HVC-50).
export const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading, profileLoading, role, email } = useAuth();

  // Wait for the session and, once signed in, for the profile that carries the role
  if (loading || (isAuthenticated && profileLoading)) return <Loading cover="page" />;

  if (!isAuthenticated) return <Navigate to={AUTH_PREFIX_PATH} replace />;

  if (!roles.CMS_ROLES.includes(role)) return <AccessDenied email={email} role={role} />;

  return children;
};

export const Views = () => {
  const location = useLocation();

  return (
    <Routes>
      <Route
        path="/"
        element={
          <>
            <Navigate to={APP_PREFIX_PATH} replace />
          </>
        }
      />
      <Route
        path={AUTH_PREFIX_PATH + "/*"}
        element={
          <>
            <CheckAlreadyAuthenticated>
              <AuthLayout />
            </CheckAlreadyAuthenticated>
          </>
        }
      />
      <Route
        path={APP_PREFIX_PATH + "/*"}
        element={
          <>
            <PrivateRoute>
              <AppLayout location={location} />
            </PrivateRoute>
          </>
        }
      />
    </Routes>
  );
};

export default Views;
